import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Grid2 from "@mui/material/Unstable_Grid2";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import ExternalCustomerAutocomplete from "../../text-fields/autocomplete/customer-autocomplete/external-customer";
import Api from "../../../../core/services/api/api";
import useSavedComponentsState from "../../../hooks/use-saved-components-state";
import useIsMounted from "../../../hooks/use-is-mounted";
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";

const ImportExternalCustomerDialog = ({open, setOpen, onSuccess}) => {
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();
    const [defaultAddUser, setDefaultAddUser] = useState(false);
    const {highestVersion: getCachedAddUser, save: saveCachedAddUser} = useSavedComponentsState({
        name: "import-external-customer-dialog-add-user",
        version: "1.0.0"
    });

    /**
     * Retrieves the cached interactive correction-enabled state from the local storage
     */
    useEffect(() => {
        void getCachedAddUser().then((cached) => {
            if (!isMounted())
                return;
            if (cached?.data !== undefined) {
                setDefaultAddUser(Boolean(cached.data));
            }
        });
    }, [getCachedAddUser, isMounted]);

    const schema = React.useMemo(() => Yup.object().shape({
        externalCustomer: Yup.mixed().required(ValidateMessages.required),
        addUser: Yup.boolean().required(ValidateMessages.required).default(false),
    }), []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        return {
            "externalCustomer": null,
            "addUser": defaultAddUser,
        }
    }, [defaultAddUser]);

    /**
     * Handles changes to the interactive correction-enabled state
     * @type {(function(*): void)|*}
     */
    const handleAddUserChange = useCallback((enabled, form) => {
        setDefaultAddUser(enabled);
        form.change('addUser', enabled);
        void saveCachedAddUser(enabled);
    }, [saveCachedAddUser]);

    /**
     * Handles the form submission.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        setImporting(true);
        const response = await Api.importCustomerFromExternal({
            internalId: values.externalCustomer.internalId,
            addUser: values.addUser,
        });
        if (!isMounted())
            return;
        setImporting(false);
        if (response?.resultFlag) {
            if (onSuccess) {
                onSuccess(response.data);
            }
            setOpen(false);
        }
    }, [onSuccess, setOpen, isMounted])

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                className={'new-Customer-dialog'}
                maxWidth={'sm'}
            >
                <DialogTitle>
                    Import External Customer
                </DialogTitle>
                <DialogContent dividers sx={{paddingBlock: 4}}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}>
                        Please select the external Customer that you want us to import.
                    </DialogContentText>
                    <Form
                        initialValues={initialValues}
                        onSubmit={submitForm}
                        validate={validate}
                        formProps={{id: 'import-customer-dialog-form'}}
                    >
                        {({values, form}) => (
                            <Grid2 spacing={2} container>
                                <Grid2 xs={12}>
                                    <ExternalCustomerAutocomplete
                                        form
                                        name={'externalCustomer'}
                                        label={'External Customer'}
                                        required={required.externalCustomer}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.Checkboxes
                                        name={'addUser'}
                                        color={'primary'}
                                        icon={<CheckboxIcon/>}
                                        checkedIcon={<ActiveCheckboxIcon/>}
                                        onChange={(e, val) => handleAddUserChange(val, form)}
                                        data={{
                                            value: values.addUser,
                                            label: 'Also create a user for this customer',
                                        }}
                                        required={required.addUser}
                                        formControlLabelProps={{
                                            sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                        }}
                                    />
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                </DialogContent>
                <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                    <Button
                        disabled={!open || importing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!open || importing}
                        variant={'dialog'}
                        color={'primary'}
                        form={'import-customer-dialog-form'}
                        type={'submit'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            importing
                                ? "Importing..."
                                : "Import"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ImportExternalCustomerDialog;
